import { Trans, t } from '@lingui/macro';
import { Settings, X, Loader2, Send } from "lucide-react";
import { useEffect, useReducer, useRef, useState } from "react";
import { useHistory } from "../../../hooks/useRouterCompat";
import Page from "../../../const/Page";
import { BotPromptSuggestion } from "../../../models/BotPromptSuggestion";
import { Room } from "../../../models/Room";
import { PublicStyle, Soribot, SoribotSimplified } from "../../../models/Soribot";
import { UUID } from "../../../models/local/Uid";
import { Fire } from "../../../services/FireClient";
import { My } from "../../../services/My";
import { Util } from "../../../services/Utils/Util";
import { getMessageText } from "../../components/MessageItem";
import BotMessageInfoModal from "../../soribots/BotMessageInfoModal";
import { BotAskModalPopover } from "./BotAskModalPopover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../../../components/ui/avatar";
import { Input } from "../../../components/ui/input";
import { useToast } from "../../../hooks/use-toast";

export const BotSoriPrivateModal: React.FC<{ bot: Soribot, room: Room | null, onClose: () => void }> = ({ bot, room, onClose }) => {
  const [show, setShow] = useState(true);
  const history = useHistory();
  const pageStyle: PublicStyle = bot.style ?? 'default-style';

  function handleClose() {
    setShow(false);
    setTimeout(onClose, 300);
  }

  function configSoribot() {
    history.push(`${Page.soribot}/details/${bot.id}`);
    onClose();
  }

  return (
    <Dialog open={show} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className={`max-w-3xl h-[85vh] flex flex-col p-0 gap-0 ${pageStyle}`}>
        <DialogHeader className="p-4 border-b flex flex-row items-center justify-between space-y-0">
          <Button variant="ghost" size="icon" onClick={handleClose}>
            <X className="h-5 w-5" />
          </Button>
          <DialogTitle className="flex flex-col items-center">
            <span className="text-sm font-semibold">{bot.name}</span>
            <span className="text-xs font-normal text-gray-500">#{room?.name}</span>
          </DialogTitle>
          <Button variant="ghost" size="icon" onClick={configSoribot}>
            <Settings className="h-5 w-5" />
          </Button>
        </DialogHeader>
        <SoribotChatContent bot={bot} room={room} />
      </DialogContent>
    </Dialog>
  );
}
var nrOfScrolls = 1;

export default BotSoriPrivateModal;

export const SoribotChatContent: React.FC<{ bot: Soribot | SoribotSimplified, room: Room | null }> = ({ bot, room }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const lastItem = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  const { toast } = useToast();

  useEffect(() => {
    if (bot.welcomeMessage) {
      const m = new ChatMessage('', bot.welcomeMessage);
      m.inProgress = false;
      setMessages([m]);
    }
  }, []);

  return (
    <>
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50" ref={scrollRef}>
        <div className="flex flex-col items-center justify-center p-4 space-y-4 text-center">
          <Avatar className="h-20 w-20">
            <AvatarImage src={bot.imageUrl ?? '/assets/img/ai-soribot.png'} />
            <AvatarFallback>{bot.name[0]}</AvatarFallback>
          </Avatar>

          {bot.childBots && bot.childBots.length > 0 ? (
            <div className="space-y-4 w-full">
              <p className="text-sm text-gray-500"><Trans>Questions will be answered by a suitable bot below</Trans></p>
              <div className="flex justify-center gap-2 flex-wrap">
                {bot.childBots.map(cb => (
                  <Avatar
                    key={cb.id}
                    className="cursor-pointer hover:ring-2 hover:ring-blue-300 transition-all"
                    onClick={() => toast({
                      title: cb.name,
                      description: cb.scope ?? undefined,
                    })}
                  >
                    <AvatarImage src={cb.imageUrl ?? '/assets/img/ai-soribot.png'} />
                    <AvatarFallback>{cb.name[0]}</AvatarFallback>
                  </Avatar>
                ))}
              </div>
              <hr className="border-gray-200" />
            </div>
          ) : (
            <p className="text-sm font-medium"><Trans>Ask questions to {bot.name}</Trans></p>
          )}
        </div>

        {messages.map((m) => getMessageItem(m))}
        <div ref={lastItem}></div>
      </div>
      <AskBotInput soribotId={bot.id} onSendStart={onSendStart} onSendDone={onSendDone} />
    </>
  );

  function getMessageItem(m: ChatMessage) {
    return (
      <div key={m.id} className="space-y-2">
        {m.prompt && (
          <div className="flex items-start gap-2 justify-end">
            <div className="bg-blue-100 text-blue-900 rounded-lg p-3 max-w-[80%] text-sm whitespace-pre-wrap">
              {m.prompt}
            </div>
            <Avatar className="h-8 w-8 mt-1">
              <AvatarImage src={My.user.photoUrl || Util.ANONYMOUS_PHOTO_URL} />
              <AvatarFallback>U</AvatarFallback>
            </Avatar>
            <BotAskModalPopover bot={bot as Soribot} msg={m} room={room} />
          </div>
        )}

        <div className="flex items-start gap-2">
          {m.inProgress ? (
            <div className="bg-white rounded-lg p-3 shadow-sm border">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          ) : m.answer === bot.welcomeMessage ? (
            <div className="bg-white rounded-lg p-3 shadow-sm border max-w-[90%] text-sm prose prose-sm" dangerouslySetInnerHTML={{ __html: getMessageText(m.answer!) }}></div>
          ) : (
            <>
              <Avatar className="h-8 w-8 mt-1">
                <AvatarImage src={getBotImageUrl(bot as Soribot, m)} />
                <AvatarFallback>B</AvatarFallback>
              </Avatar>
              <div className="bg-white rounded-lg p-3 shadow-sm border max-w-[90%] space-y-1">
                <div className="text-sm prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: getMessageText(m.answer!) }}></div>
                {My.isLoggedIn() && m.botMessageId && (
                  <div className="text-xs text-gray-400 pt-1 border-t mt-2">
                    <BotMessageInfoModal botMessageId={m.botMessageId} bmsg={null} />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  function getBotImageUrl(bot: Soribot, chatMsg: ChatMessage) {
    if (chatMsg.soribotId && (bot.childBots && bot.childBots.length > 0)) {
      const cb = bot.childBots.find(cb => cb.id == chatMsg.soribotId);
      return cb?.imageUrl ?? '/assets/img/ai-soribot.png';
    }
    return bot.imageUrl ?? '/assets/img/ai-soribot.png';
  }

  function onSendStart(m: ChatMessage) {
    setMessages(p => [...p, m])
    scrollViewToBottom();
  }

  function onSendDone() {
    scrollViewToBottom();
    forceUpdate();
  }

  function scrollViewToBottom() {
    if (lastItem.current) {
      lastItem.current.scrollIntoView({ behavior: "smooth" });
    }
  }
}

export const AskBotInput: React.FC<{ soribotId: string, onSendStart: (m: ChatMessage) => void, onSendDone: () => void }> = ({ soribotId, onSendStart, onSendDone }) => {
  const { toast } = useToast();
  const [prompt, setPrompt] = useState("");
  const [inProgress, setInProgress] = useState(false);
  const [suggestions, setSuggestions] = useState<BotPromptSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState<BotPromptSuggestion[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  useEffect(() => {
    setInProgress(true);
    Fire.bot.getPromptSuggestions(soribotId, !My.isLoggedIn()).then((sugs) => {
      setSuggestions(sugs.sort((a, b) => a.prompt.localeCompare(b.prompt)));
    }).finally(() => {
      setInProgress(false);
    });
  }, [soribotId])

  useEffect(() => {
    if (selectedIndex !== -1 && showSuggestions[selectedIndex]) {
      const el = document.getElementById(`sug-${showSuggestions[selectedIndex].id}`);
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex])

  return (
    <div className="border-t bg-white p-2 shrink-0">
      {inProgress && <div className="h-1 w-full overflow-hidden mb-2"><div className="h-full bg-blue-500 animate-pulse"></div></div>}

      {showSuggestions.length > 0 && (
        <div className="absolute bottom-16 left-2 right-2 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto z-50">
          <div className="flex items-center justify-between p-2 border-b bg-gray-50 text-xs">
            <div>
              <span className="font-semibold"><Trans>Suggestions</Trans></span>
              <span className="text-gray-500 ml-2"><Trans>Use up and down arrow to select</Trans></span>
            </div>
            <div className="flex gap-2">
              {showSuggestions.length < suggestions.length && <button className="text-blue-600 hover:underline" onClick={showAllSugs}>Show All</button>}
              <button className="text-gray-500 hover:text-gray-900" onClick={() => setShowSuggestions([])}>Close</button>
            </div>
          </div>
          <div>
            {showSuggestions.map((s, i) => (
              <div
                key={i}
                id={`sug-${s.id}`}
                className={`p-2 text-sm cursor-pointer hover:bg-gray-100 ${i === selectedIndex ? 'bg-blue-50 text-blue-700' : ''}`}
                onClick={() => selectSuggestion(s)}
              >
                {s.prompt}
              </div>
            ))}
          </div>
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit(prompt);
        }}
        className="flex items-center gap-2"
      >
        <Input
          id='prompt-input'
          value={prompt}
          onChange={e => changePrompt(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t`Send a message`}
          className="flex-1"
          autoComplete="off"
        />
        <Button type="submit" size="icon" disabled={!prompt.trim() || inProgress}>
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (showSuggestions.length > 0 && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      e.preventDefault();
      let index = -1;
      if (e.key === 'ArrowUp') {
        index = selectedIndex == -1 ? showSuggestions.length - 1 : Math.max(selectedIndex - 1, 0);
      } else if (e.key === 'ArrowDown') {
        index = Math.min(selectedIndex + 1, showSuggestions.length - 1);
      }
      setPrompt(showSuggestions[index]?.prompt ?? prompt);
      setSelectedIndex(index);
    }
  }

  function changePrompt(p: string) {
    setPrompt(p);
    if (suggestions.length === 0)
      return;
    else if (p.length === 0) {
      reset();
    } else if (suggestions.length < 500 || p.length > 1) {
      const list = suggestions.filter(s => s.prompt.toLowerCase().includes(p.toLowerCase()));
      setShowSuggestions(list);
    }
  }

  function selectSuggestion(s: BotPromptSuggestion) {
    submit(s.prompt);
    reset();
  }

  async function submit(p: string) {
    const m = new ChatMessage(p);
    if (!m.prompt) {
      return;
    }

    onSendStart(m);
    reset();

    try {
      const sug = suggestions.find(s => s.prompt.trim() == m.prompt);
      const r = await Fire.bot.askSoribot({ soribotId: soribotId, prompt: m.prompt, suggestionId: sug?.id });
      m.answer = r.answer;
      m.botMessageId = r.botMessageId;
      m.soribotId = r.soribotId;
    } catch (error) {
      const e = error as Error;
      toast({ title: t`Error`, description: e.message });
    } finally {
      m.inProgress = false;
    }

    onSendDone();
  }

  function reset() {
    setPrompt('');
    setShowSuggestions([]);
    setSelectedIndex(-1);
  }

  function showAllSugs() {
    setShowSuggestions(suggestions);
  }
}

export class ChatMessage {
  id: string = UUID.get();
  created = new Date();
  inProgress = true;
  soribotId?: string;
  constructor(public prompt: string, public answer: string | null = null, public botMessageId: string | null = null) {
    this.prompt = prompt.trim();
  }
}